import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Header from './Header';
import LeadsPage from './LeadsPage';
import ProcessSystemsView from './ProcessSystemsView';
import BackofficeExamsCalendar from './BackofficeExamsCalendar';
import ErfolgeTab from './ErfolgeTab';
import GoogleCalendarWidget from './GoogleCalendarWidget';

const BackofficeDashboard = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('onboarding');
  
  // Leads-Navigation aus Benachrichtigungs-Glocke
  React.useEffect(() => {
    const handler = () => setTab('leads');
    window.addEventListener('navigateToLeads', handler);
    return () => window.removeEventListener('navigateToLeads', handler);
  }, []);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'vertriebler', managed_by: '' });
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  // Reassignment state: { vertrieblerId, selectedTeamleiterId }
  const [reassign, setReassign] = useState({ vertrieblerId: null, selectedTeamleiterId: '' });
  const [reassigning, setReassigning] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  // Role change state
  const [roleChange, setRoleChange] = useState({ userId: null, currentRole: '', newRole: '' });
  const [changingRole, setChangingRole] = useState(false);
  // Edit user state
  const [editUser, setEditUser] = useState(null); // { id, name, email }
  const [editForm, setEditForm] = useState({ name: '', email: '' });
  const [editSaving, setEditSaving] = useState(false);
  // Team accordion state
  const [expandedTeams, setExpandedTeams] = useState({});
  // Exam filter state
  const [examFilter, setExamFilter] = useState('all');
  // Reschedule reset state
  const [resettingReschedule, setResettingReschedule] = useState(null);

  // Process Systems state for Backoffice
  const [selectedProcessSystem, setSelectedProcessSystem] = useState(null);
  const [showSystemModal, setShowSystemModal] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [showFaqModal, setShowFaqModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [systemForm, setSystemForm] = useState({ name: '', description: '', type: 'client', system_url: '', order: 0, logo_url: '' });
  const [guideForm, setGuideForm] = useState({ title: '', description: '', steps: [], order: 0 });
  const [faqForm, setFaqForm] = useState({ question: '', answer: '', category: '', order: 0 });
  const [editingSystem, setEditingSystem] = useState(null);
  const [editingGuide, setEditingGuide] = useState(null);
  const [editingFaq, setEditingFaq] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const psRefreshFn = React.useRef();

  const load = async () => {
    // Seite sofort anzeigen, Daten im Hintergrund nachladen
    setLoading(false);
    try {
      const r = await axios.get(`${API}/backoffice/dashboard`);
      setData(r.data);
    } catch(e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError(''); setFormSuccess('');
    if (!form.name || !form.email) { setFormError('Name und E-Mail sind Pflichtfelder.'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/backoffice/create-vertriebler`, {
        name: form.name,
        email: form.email,
        role: form.role,
        managed_by: form.managed_by || null,
        password: Math.random().toString(36).slice(-10),
      });
      setFormSuccess(`Account für ${form.name} wurde erstellt. Setup-E-Mail wurde versendet.`);
      setForm({ name: '', email: '', role: 'vertriebler', managed_by: '' });
      load();
    } catch(e) {
      setFormError(e.response?.data?.detail || 'Fehler beim Erstellen des Accounts.');
    }
    setCreating(false);
  };

  const startReassign = (vertrieblerId, currentTeamleiterId) => {
    setReassign({ vertrieblerId, selectedTeamleiterId: currentTeamleiterId || '' });
  };

  const cancelReassign = () => setReassign({ vertrieblerId: null, selectedTeamleiterId: '' });

  const confirmReassign = async (vertrieblerId) => {
    setReassigning(true);
    try {
      await axios.put(`${API}/backoffice/assign-teamleiter`, {
        vertriebler_id: vertrieblerId,
        teamleiter_id: reassign.selectedTeamleiterId || null,
      });
      toast.success('Zuweisung erfolgreich aktualisiert.');
      cancelReassign();
      load();
    } catch(e) {
      toast.error(e.response?.data?.detail || 'Fehler bei der Zuweisung.');
    }
    setReassigning(false);
  };

  const handleResendSetup = async (userId, userName) => {
    setResendingId(userId);
    try {
      await axios.post(`${API}/backoffice/resend-setup-email/${userId}`);
      toast.success(`Erinnerungs-E-Mail an ${userName} gesendet.`);
    } catch(e) {
      toast.error(e.response?.data?.detail || 'E-Mail konnte nicht gesendet werden.');
    }
    setResendingId(null);
  };

  const startRoleChange = (userId, currentRole) => {
    const newRole = currentRole === 'vertriebler' ? 'teamleiter' : 'vertriebler';
    setRoleChange({ userId, currentRole, newRole });
  };

  const confirmRoleChange = async () => {
    setChangingRole(true);
    try {
      const r = await axios.put(`${API}/backoffice/change-role/${roleChange.userId}`, { role: roleChange.newRole });
      toast.success(r.data.message || 'Rolle erfolgreich geändert.');
      setRoleChange({ userId: null, currentRole: '', newRole: '' });
      load();
    } catch(e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Ändern der Rolle.');
    }
    setChangingRole(false);
  };

  const openEditUser = (user) => {
    setEditUser(user);
    setEditForm({ name: user.name, email: user.email });
  };

  const saveEditUser = async () => {
    if (!editUser) return;
    setEditSaving(true);
    try {
      await axios.put(`${API}/backoffice/edit-user/${editUser.id}`, editForm);
      toast.success('Accountdaten aktualisiert.');
      setEditUser(null);
      load();
    } catch(e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern.');
    }
    setEditSaving(false);
  };

  const toggleTeam = (teamId) => {
    setExpandedTeams(prev => ({ ...prev, [teamId]: !prev[teamId] }));
  };

  const handleResetReschedule = async (userId, userName) => {
    setResettingReschedule(userId);
    try {
      await axios.post(`${API}/appointments/users/${userId}/reset-reschedule`);
      toast.success(`Verschiebungslimit für ${userName} zurückgesetzt.`);
      load();
    } catch(e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Zurücksetzen.');
    }
    setResettingReschedule(null);
  };

  // Process Systems handlers for Backoffice
  const psHandleCreate = async () => {
    try {
      await axios.post(`${API}/process-systems`, systemForm);
      setShowSystemModal(false);
      setSystemForm({ name: '', description: '', type: 'client', system_url: '', order: 0, logo_url: '' });
      if (psRefreshFn.current) psRefreshFn.current();
      toast.success('System erfolgreich erstellt!');
    } catch(e) { toast.error('Fehler beim Erstellen des Systems'); }
  };
  const psHandleUpdate = async () => {
    try {
      await axios.put(`${API}/process-systems/${editingSystem.id}`, systemForm);
      setShowSystemModal(false); setEditingSystem(null);
      setSystemForm({ name: '', description: '', type: 'client', system_url: '', order: 0, logo_url: '' });
      if (psRefreshFn.current) psRefreshFn.current();
      toast.success('System erfolgreich aktualisiert!');
    } catch(e) { toast.error('Fehler beim Aktualisieren des Systems'); }
  };
  const psHandleCreateGuide = async () => {
    try {
      await axios.post(`${API}/process-systems/${selectedProcessSystem.id}/guides`, { ...guideForm, system_id: selectedProcessSystem.id });
      setShowGuideModal(false); setGuideForm({ title: '', content: '', order: 0 });
      if (psRefreshFn.current) psRefreshFn.current();
      toast.success('Anleitung erfolgreich erstellt!');
    } catch(e) { toast.error('Fehler beim Erstellen der Anleitung'); }
  };
  const psHandleUpdateGuide = async () => {
    try {
      await axios.put(`${API}/process-guides/${editingGuide.id}`, guideForm);
      setShowGuideModal(false); setEditingGuide(null); setGuideForm({ title: '', content: '', order: 0 });
      toast.success('Anleitung aktualisiert!');
    } catch(e) { toast.error('Fehler beim Aktualisieren der Anleitung'); }
  };
  const psHandleCreateFaq = async () => {
    try {
      await axios.post(`${API}/process-systems/${selectedProcessSystem.id}/faqs`, { ...faqForm, system_id: selectedProcessSystem.id });
      setShowFaqModal(false); setFaqForm({ question: '', answer: '', category: '', order: 0 });
      if (psRefreshFn.current) psRefreshFn.current();
      toast.success('FAQ erfolgreich erstellt!');
    } catch(e) { toast.error('Fehler beim Erstellen der FAQ'); }
  };
  const psHandleUpdateFaq = async () => {
    try {
      await axios.put(`${API}/process-faqs/${editingFaq.id}`, faqForm);
      setShowFaqModal(false); setEditingFaq(null); setFaqForm({ question: '', answer: '', category: '', order: 0 });
      toast.success('FAQ aktualisiert!');
    } catch(e) { toast.error('Fehler beim Aktualisieren der FAQ'); }
  };
  const psHandleUploadDoc = async () => {
    if (!uploadFile) { toast.error('Bitte Datei auswählen'); return; }
    const fd = new FormData();
    fd.append('file', uploadFile);
    if (uploadDescription) fd.append('description', uploadDescription);
    try {
      setUploading(true);
      await axios.post(`${API}/process-systems/${selectedProcessSystem.id}/documents/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setShowDocumentModal(false); setUploadFile(null); setUploadDescription('');
      if (psRefreshFn.current) psRefreshFn.current();
      toast.success('Dokument hochgeladen!');
    } catch(e) { toast.error('Fehler beim Hochladen'); } finally { setUploading(false); }
  };
  const psHandleDeleteDoc = async (docId) => {
    if (!window.confirm('Dokument wirklich löschen?')) return;
    try { await axios.delete(`${API}/process-documents/${docId}`); toast.success('Dokument gelöscht!'); }
    catch(e) { toast.error('Fehler beim Löschen'); }
  };
  const psHandleDeleteSystem = async (sysId) => {
    if (!window.confirm('System wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/process-systems/${sysId}`);
      if (selectedProcessSystem?.id === sysId) setSelectedProcessSystem(null);
      if (psRefreshFn.current) psRefreshFn.current();
      toast.success('System gelöscht!');
    } catch(e) { toast.error('Fehler beim Löschen des Systems'); }
  };

  const tabs = [
    { key: 'onboarding', label: 'Onboarding', icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg> },
    { key: 'create',     label: 'Account anlegen', icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"/></svg> },
    { key: 'team',       label: 'Team-Übersicht', icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg> },
    { key: 'exams',      label: 'Termine', icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg> },
    { key: 'erfolge',    label: 'Erfolge', icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/></svg> },
    { key: 'leads',      label: 'Leads', icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg> },
  ];

  // testid map for each tab key
  const tabTestIds = {
    onboarding: 'backoffice-tab-onboarding',
    create: 'backoffice-tab-account',
    team: 'backoffice-tab-team',
    exams: 'backoffice-tab-termine',
    erfolge: 'backoffice-tab-erfolge',
    leads: 'backoffice-tab-leads',
  };

  const examLabel = {
    exam_passed:    { text: 'Bestanden',      cls: 'bg-green-100 text-green-700' },
    exam_booked:    { text: 'Termin gebucht', cls: 'bg-blue-100 text-blue-700' },
    ready_for_exam: { text: 'Bereit',         cls: 'bg-indigo-100 text-indigo-700' },
    exam_failed:    { text: 'Nicht bestanden',cls: 'bg-red-100 text-red-700' },
  };
  const getExamBadge = (s) => examLabel[s] || { text: 'Noch nicht bereit', cls: 'bg-gray-100 text-gray-500' };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Global Header with logo, user info and logout */}
      <Header />

      {/* Role Change Confirmation Modal */}
      {roleChange.userId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Rolle ändern</h3>
            <p className="text-sm text-gray-600 mb-5">
              Soll die Rolle von{' '}
              <span className="font-semibold">
                {data?.onboarding?.find(u => u.id === roleChange.userId)?.name ||
                 data?.teams?.flatMap(t => t.members).find(m => m.id === roleChange.userId)?.name ||
                 data?.unassigned?.find(u => u.id === roleChange.userId)?.name || 'diesem Benutzer'}
              </span>{' '}
              von{' '}
              <span className="font-medium text-orange-600 capitalize">{roleChange.currentRole}</span>
              {' '}zu{' '}
              <span className="font-medium text-blue-600 capitalize">{roleChange.newRole === 'teamleiter' ? 'Teamleiter' : 'Vertriebler'}</span>
              {' '}geändert werden?
            </p>
            {roleChange.newRole === 'teamleiter' && (
              <p className="text-xs text-blue-600 bg-blue-50 rounded-lg px-3 py-2 mb-4">
                Der Benutzer erhält Zugang zu den Teamleiter-Schulungen und verlässt sein aktuelles Team.
              </p>
            )}
            <div className="flex gap-3 justify-end">
              <button onClick={() => setRoleChange({ userId: null, currentRole: '', newRole: '' })}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium">
                Abbrechen
              </button>
              <button onClick={confirmRoleChange} disabled={changingRole}
                data-testid="role-change-confirm-btn"
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">
                {changingRole ? 'Wird geändert...' : 'Ja, Rolle ändern'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editUser && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Accountdaten bearbeiten</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input type="text" value={editForm.name} onChange={e => setEditForm(f => ({...f, name: e.target.value}))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  data-testid="edit-user-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail</label>
                <input type="email" value={editForm.email} onChange={e => setEditForm(f => ({...f, email: e.target.value}))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  data-testid="edit-user-email-input" />
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button onClick={() => setEditUser(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium">
                Abbrechen
              </button>
              <button onClick={saveEditUser} disabled={editSaving}
                data-testid="edit-user-save-btn"
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">
                {editSaving ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page title */}
      <div className="bg-white border-b border-gray-100 px-8 py-4 shadow-sm">
        <div className="max-w-6xl">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Backoffice</h1>
          <p className="text-sm text-slate-500 mt-0.5">Onboarding & Vertriebler-Verwaltung</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-100 px-8">
        <div className="flex gap-1 max-w-6xl overflow-x-auto">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              data-testid={tabTestIds[t.key] || `backoffice-tab-${t.key}`}
              className={`flex items-center gap-2 py-3.5 px-3 border-b-2 text-sm font-medium transition-all whitespace-nowrap ${tab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'}`}>
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-8 py-8 max-w-6xl">
        {!data && (
          <div className="flex items-center gap-3 text-gray-500 py-8">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
            <span className="text-sm">Lade Daten...</span>
          </div>
        )}
        {(data || true) && (
          <>
            {/* ── ONBOARDING ÜBERSICHT ── */}
            {tab === 'onboarding' && (
              <div>
                {(() => {
                  const all = data?.onboarding ?? [];
                  const activated = all.filter(u => u.setup_completed).length;
                  const notActivated = all.filter(u => !u.setup_completed).length;
                  const inTraining = all.filter(u => u.started_learning && !['ready_for_exam', 'exam_passed', 'exam_booked'].includes(u.exam_status)).length;
                  const examReady = all.filter(u => ['ready_for_exam', 'exam_booked'].includes(u.exam_status)).length;
                  const examPassed = all.filter(u => u.exam_status === 'exam_passed').length;
                  const needsAction = all.filter(u => !u.setup_completed || (!u.started_learning && u.setup_completed)).length;
                  return (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                      <div className="stat-card bg-white rounded-xl border-l-4 border-l-slate-400 shadow-sm p-4" data-testid="stat-total">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Gesamt</p>
                          <div className="p-1.5 bg-slate-100 rounded-lg"><svg className="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg></div>
                        </div>
                        <p className="text-2xl font-bold text-slate-800">{all.length}</p>
                        <p className="text-xs text-slate-400 mt-0.5">Vertriebspartner</p>
                      </div>
                      <div className="stat-card bg-white rounded-xl border-l-4 border-l-emerald-500 shadow-sm p-4" data-testid="stat-activated">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Aktiviert</p>
                          <div className="p-1.5 bg-emerald-50 rounded-lg"><svg className="w-3.5 h-3.5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg></div>
                        </div>
                        <p className="text-2xl font-bold text-emerald-600">{activated}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{notActivated} ausstehend</p>
                      </div>
                      <div className="stat-card bg-white rounded-xl border-l-4 border-l-blue-500 shadow-sm p-4" data-testid="stat-in-training">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">In Schulung</p>
                          <div className="p-1.5 bg-blue-50 rounded-lg"><svg className="w-3.5 h-3.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg></div>
                        </div>
                        <p className="text-2xl font-bold text-blue-600">{inTraining}</p>
                        <p className="text-xs text-slate-400 mt-0.5">aktiv lernend</p>
                      </div>
                      <div className="stat-card bg-white rounded-xl border-l-4 border-l-indigo-500 shadow-sm p-4" data-testid="stat-exam-ready">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Prüfungsbereit</p>
                          <div className="p-1.5 bg-indigo-50 rounded-lg"><svg className="w-3.5 h-3.5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/></svg></div>
                        </div>
                        <p className="text-2xl font-bold text-indigo-600">{examReady}</p>
                        <p className="text-xs text-slate-400 mt-0.5">bereit / gebucht</p>
                      </div>
                      <div className="stat-card bg-white rounded-xl border-l-4 border-l-teal-500 shadow-sm p-4" data-testid="stat-exam-passed">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Bestanden</p>
                          <div className="p-1.5 bg-teal-50 rounded-lg"><svg className="w-3.5 h-3.5 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/></svg></div>
                        </div>
                        <p className="text-2xl font-bold text-teal-600">{examPassed}</p>
                        <p className="text-xs text-slate-400 mt-0.5">Abschluss erhalten</p>
                      </div>
                      <div className="stat-card bg-white rounded-xl border-l-4 border-l-red-400 shadow-sm p-4" data-testid="stat-needs-action">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Handlungsbedarf</p>
                          <div className="p-1.5 bg-red-50 rounded-lg"><svg className="w-3.5 h-3.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg></div>
                        </div>
                        <p className={`text-2xl font-bold ${needsAction > 0 ? 'text-red-600' : 'text-slate-400'}`}>{needsAction}</p>
                        <p className="text-xs text-slate-400 mt-0.5">inaktiv / nicht gestartet</p>
                      </div>
                    </div>
                  );
                })()}

                <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                  <table className="w-full text-sm min-w-[900px]">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        <th className="text-left px-5 py-3">Name</th>
                        <th className="text-left px-5 py-3">Account</th>
                        <th className="text-left px-5 py-3">Schulung</th>
                        <th className="text-left px-5 py-3">Vorgesetzter / Team</th>
                        <th className="text-left px-5 py-3">Abschluss</th>
                        <th className="text-left px-5 py-3">Zuletzt aktiv</th>
                        <th className="text-left px-5 py-3">Rolle</th>
                        <th className="text-left px-5 py-3">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {(data?.onboarding ?? []).map(u => (
                        <tr key={u.id} className="hover:bg-gray-50 transition-colors" data-testid={`onboarding-row-${u.id}`}>
                          <td className="px-5 py-3">
                            <p className="font-medium text-gray-900">{u.name}</p>
                            <p className="text-xs text-gray-400">{u.email}</p>
                            {u.role === 'teamleiter' && (
                              <span className="inline-flex items-center text-xs font-medium text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded-full mt-0.5">Teamleiter</span>
                            )}
                          </td>
                          <td className="px-5 py-3">
                            {u.setup_completed
                              ? <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Aktiviert</span>
                              : (
                                <div className="flex items-center gap-2">
                                  <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">Ausstehend</span>
                                  <button
                                    onClick={() => handleResendSetup(u.id, u.name)}
                                    disabled={resendingId === u.id}
                                    title="Setup-E-Mail erneut senden"
                                    className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50 whitespace-nowrap"
                                    data-testid={`resend-btn-${u.id}`}>
                                    {resendingId === u.id ? 'Sende...' : 'Reminder'}
                                  </button>
                                </div>
                              )}
                          </td>
                          <td className="px-5 py-3">
                            {u.started_learning
                              ? <span className="text-xs text-blue-700 font-medium">{u.completed_modules} Module abgeschlossen</span>
                              : <span className="text-xs text-gray-400">Noch nicht gestartet</span>}
                          </td>
                          <td className="px-5 py-3 text-xs text-gray-600">
                            {u.role === 'teamleiter'
                              ? <span className="text-xs text-blue-600 font-medium italic">— Teamleiter</span>
                              : u.managed_by?.name
                                ? <span>{u.managed_by.name}</span>
                                : reassign.vertrieblerId === u.id
                                ? (
                                  <div className="flex items-center gap-1">
                                    <select
                                      value={reassign.selectedTeamleiterId}
                                      onChange={e => setReassign(r => ({ ...r, selectedTeamleiterId: e.target.value }))}
                                      className="border border-gray-200 rounded px-1 py-0.5 text-xs">
                                      <option value="">Bitte wählen...</option>
                                      {(data?.teamleiter_list ?? []).map(tl => (
                                        <option key={tl.id} value={tl.id}>{tl.name}</option>
                                      ))}
                                    </select>
                                    <button onClick={() => confirmReassign(u.id)} disabled={reassigning}
                                      className="text-xs bg-blue-600 text-white px-1.5 py-0.5 rounded hover:bg-blue-700 disabled:opacity-50">OK</button>
                                    <button onClick={cancelReassign} className="text-xs text-gray-400 hover:text-gray-600">✕</button>
                                  </div>
                                )
                                : <button onClick={() => startReassign(u.id, '')}
                                    className="text-xs text-blue-500 hover:text-blue-700 font-medium"
                                    data-testid={`onboarding-assign-${u.id}`}>
                                    Zuweisen
                                  </button>
                            }
                          </td>
                          <td className="px-5 py-3">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getExamBadge(u.exam_status).cls}`}>
                              {getExamBadge(u.exam_status).text}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-xs text-gray-400">
                            {u.last_active ? new Date(u.last_active).toLocaleDateString('de-DE') : '—'}
                          </td>
                          <td className="px-5 py-3">
                            <button
                              onClick={() => startRoleChange(u.id, u.role || 'vertriebler')}
                              title={u.role === 'vertriebler' ? 'Zum Teamleiter befördern' : 'Zu Vertriebler zurückstufen'}
                              className={`text-xs font-medium border px-2.5 py-1 rounded-full transition-colors ${
                                u.role === 'teamleiter'
                                  ? 'text-blue-600 border-blue-200 hover:bg-blue-50'
                                  : 'text-indigo-600 border-indigo-200 hover:bg-indigo-50'
                              }`}
                              data-testid={`onboarding-role-btn-${u.id}`}>
                              {u.role === 'teamleiter' ? 'TL → VB' : 'VB → TL'}
                            </button>
                          </td>
                          <td className="px-5 py-3">
                            <button
                              onClick={() => openEditUser(u)}
                              title="Accountdaten bearbeiten"
                              className="text-xs font-medium border px-2.5 py-1 rounded-full transition-colors text-blue-600 border-blue-200 hover:bg-blue-50"
                              data-testid={`onboarding-edit-btn-${u.id}`}>
                              Bearbeiten
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── VERTRIEBLER ANLEGEN ── */}
            {tab === 'create' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <div className="bg-white rounded-xl border border-gray-200 p-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-6">Neuen Account anlegen</h2>
                    <form onSubmit={handleCreate} className="space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                        <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                          placeholder="Vorname Nachname" data-testid="backoffice-name-input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail *</label>
                        <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                          placeholder="name@beispiel.de" data-testid="backoffice-email-input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Rolle</label>
                        <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                          data-testid="backoffice-role-select">
                          <option value="vertriebler">Vertriebler</option>
                          <option value="teamleiter">Teamleiter</option>
                        </select>
                      </div>
                      {form.role === 'vertriebler' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Teamleiter zuweisen</label>
                          <select value={form.managed_by} onChange={e => setForm({...form, managed_by: e.target.value})}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                            data-testid="backoffice-teamleiter-select">
                            <option value="">Noch nicht zuweisen</option>
                            {(data?.teamleiter_list ?? []).map(tl => (
                              <option key={tl.id} value={tl.id}>{tl.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {formError && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{formError}</p>}
                      {formSuccess && <p className="text-sm text-green-700 bg-green-50 rounded-lg px-3 py-2">{formSuccess}</p>}
                      <button type="submit" disabled={creating}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
                        data-testid="backoffice-create-btn">
                        {creating ? 'Wird erstellt...' : 'Account erstellen & Setup-E-Mail senden'}
                      </button>
                    </form>
                  </div>
                  <p className="text-xs text-gray-400 mt-3 text-center">Der neue Account erhält automatisch eine Setup-E-Mail zur Passwort-Erstellung.</p>
                </div>

                {/* Zuletzt angelegte Accounts */}
                <div>
                  <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="recent-accounts">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide">Zuletzt angelegte Accounts</h3>
                    {(() => {
                      const sorted = [...(data?.onboarding ?? [])]
                        .filter(u => u.created_at)
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                        .slice(0, 8);
                      const noDate = (data?.onboarding ?? []).filter(u => !u.created_at);
                      const recentList = sorted.length > 0 ? sorted : (data?.onboarding ?? []).slice(0, 8);
                      return recentList.length === 0 ? (
                        <p className="text-sm text-gray-400">Noch keine Accounts vorhanden.</p>
                      ) : (
                        <div className="space-y-3">
                          {recentList.map(u => (
                            <div key={u.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0" data-testid={`recent-account-${u.id}`}>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 truncate">{u.name}</p>
                                <p className="text-xs text-gray-400 truncate">{u.email}</p>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                {u.setup_completed
                                  ? <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Aktiviert</span>
                                  : <span className="text-xs font-medium text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">Ausstehend</span>
                                }
                                {u.created_at && (
                                  <span className="text-xs text-gray-400">
                                    {new Date(u.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Quick stats */}
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                      <p className="text-2xl font-bold text-blue-600">{data?.onboarding?.length ?? 0}</p>
                      <p className="text-xs text-gray-500 mt-0.5">Accounts gesamt</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                      <p className="text-2xl font-bold text-orange-600">{data?.onboarding?.filter(u => !u.setup_completed).length ?? 0}</p>
                      <p className="text-xs text-gray-500 mt-0.5">Warten auf Aktivierung</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── TEAM-ÜBERSICHT ── */}
            {tab === 'team' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-400">Klicke auf einen Teamleiter um die Vertriebler aufzuklappen.</p>
                  <button onClick={() => {
                    const allIds = (data?.teams ?? []).reduce((acc, t) => ({ ...acc, [t.teamleiter.id]: true }), {});
                    const allExpanded = Object.keys(allIds).every(id => expandedTeams[id]);
                    setExpandedTeams(allExpanded ? {} : allIds);
                  }} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                    {Object.keys(expandedTeams).length > 0 && Object.values(expandedTeams).some(v => v) ? 'Alle zuklappen' : 'Alle aufklappen'}
                  </button>
                </div>
                {(data?.teams ?? []).map(team => (
                  <div key={team.teamleiter.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    {/* Teamleiter Header - clickable accordion */}
                    <button
                      onClick={() => toggleTeam(team.teamleiter.id)}
                      className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
                      data-testid={`team-toggle-${team.teamleiter.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-bold text-blue-700">{team.teamleiter.name?.charAt(0) || 'T'}</span>
                        </div>
                        <div className="text-left">
                          <span className="text-sm font-bold text-gray-900">{team.teamleiter.name}</span>
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full ml-2">Teamleiter</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">{team.members.length} Vertriebler</span>
                        <svg className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${expandedTeams[team.teamleiter.id] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>
                    {/* Collapsible member list */}
                    {expandedTeams[team.teamleiter.id] && (
                      <div className="border-t border-gray-100 px-6 py-3">
                        {team.members.length === 0 ? (
                          <p className="text-xs text-gray-400 py-2">Keine Vertriebler zugewiesen</p>
                        ) : (
                          <div className="space-y-2">
                            {team.members.map(m => (
                              <div key={m.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2.5 gap-3"
                                data-testid={`team-member-${m.id}`}>
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm text-gray-800 block truncate">{m.name}</span>
                                  <span className="text-xs text-gray-400 block truncate">{m.email}</span>
                                </div>
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${getExamBadge(m.exam_status).cls}`}>
                                  {getExamBadge(m.exam_status).text}
                                </span>
                                {reassign.vertrieblerId === m.id ? (
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <select
                                      value={reassign.selectedTeamleiterId}
                                      onChange={e => setReassign(r => ({ ...r, selectedTeamleiterId: e.target.value }))}
                                      className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                                      data-testid={`reassign-select-${m.id}`}>
                                      <option value="">Zuweisung aufheben</option>
                                      {(data?.teamleiter_list ?? []).map(tl => (
                                        <option key={tl.id} value={tl.id}>{tl.name}</option>
                                      ))}
                                    </select>
                                    <button onClick={() => confirmReassign(m.id)} disabled={reassigning}
                                      className="text-xs bg-blue-600 text-white px-2.5 py-1 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                      data-testid={`reassign-confirm-${m.id}`}>
                                      {reassigning ? '...' : 'OK'}
                                    </button>
                                    <button onClick={cancelReassign}
                                      className="text-xs text-gray-500 hover:text-gray-700 px-1"
                                      data-testid={`reassign-cancel-${m.id}`}>
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <button onClick={() => openEditUser(m)}
                                      title="Accountdaten bearbeiten"
                                      className="text-xs text-gray-500 hover:text-blue-600 border border-gray-200 hover:border-blue-300 px-2 py-0.5 rounded-full"
                                      data-testid={`edit-user-btn-${m.id}`}>
                                      Bearbeiten
                                    </button>
                                    <button onClick={() => startReassign(m.id, team.teamleiter.id)}
                                      title="Zu anderem Teamleiter verschieben"
                                      className="text-xs text-blue-600 hover:text-blue-800 font-medium border border-blue-200 hover:border-blue-400 px-2 py-0.5 rounded-full"
                                      data-testid={`reassign-btn-${m.id}`}>
                                      Verschieben
                                    </button>
                                    <button onClick={() => startRoleChange(m.id, m.role || 'vertriebler')}
                                      title="Rolle ändern"
                                      className="text-xs text-purple-600 hover:text-purple-800 font-medium border border-purple-200 hover:border-purple-400 px-2 py-0.5 rounded-full"
                                      data-testid={`role-change-btn-${m.id}`}>
                                      Befördern
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {(data?.unassigned ?? []).length > 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
                    <p className="text-sm font-semibold text-orange-800 mb-3">Ohne Teamleiter ({data.unassigned.length})</p>
                    <div className="space-y-2">
                      {data.unassigned.map(u => (
                        <div key={u.id} className="flex items-center justify-between bg-white border border-orange-200 rounded-lg px-3 py-2.5 gap-3"
                          data-testid={`unassigned-${u.id}`}>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-orange-800 block truncate">{u.name}</span>
                            <span className="text-xs text-gray-400 block truncate">{u.email}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button onClick={() => openEditUser(u)}
                              className="text-xs text-gray-500 hover:text-blue-600 border border-gray-200 hover:border-blue-300 px-2 py-0.5 rounded-full"
                              data-testid={`edit-unassigned-btn-${u.id}`}>
                              Bearbeiten
                            </button>
                            {reassign.vertrieblerId === u.id ? (
                              <div className="flex items-center gap-2">
                                <select
                                  value={reassign.selectedTeamleiterId}
                                  onChange={e => setReassign(r => ({ ...r, selectedTeamleiterId: e.target.value }))}
                                  className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                                  data-testid={`reassign-select-unassigned-${u.id}`}>
                                  <option value="">Bitte wählen...</option>
                                  {(data?.teamleiter_list ?? []).map(tl => (
                                    <option key={tl.id} value={tl.id}>{tl.name}</option>
                                  ))}
                                </select>
                                <button onClick={() => confirmReassign(u.id)} disabled={reassigning}
                                  className="text-xs bg-blue-600 text-white px-2.5 py-1 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                  data-testid={`reassign-confirm-unassigned-${u.id}`}>
                                  {reassigning ? '...' : 'Zuweisen'}
                                </button>
                                <button onClick={cancelReassign}
                                  className="text-xs text-gray-500 hover:text-gray-700 px-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                              </div>
                            ) : (
                              <button onClick={() => startReassign(u.id, '')}
                                className="text-xs text-blue-600 hover:text-blue-800 font-medium flex-shrink-0"
                                data-testid={`assign-btn-${u.id}`}>
                                Teamleiter zuweisen
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── ABSCHLUSSGESPRÄCHE ── */}
            {tab === 'exams' && (
              <div>
                <GoogleCalendarWidget onSyncComplete={load} />
                <BackofficeExamsCalendar
                  appointments={data?.appointments ?? []}
                  handleResetReschedule={handleResetReschedule}
                  resettingReschedule={resettingReschedule}
                />
              </div>
            )}

            {/* ── PROZESSE & SYSTEME ── */}
            {tab === 'leads' && <LeadsPage role={user?.role} />}
        {tab === 'processes' && (
              <ProcessSystemsView
                user={user}
                selectedSystem={selectedProcessSystem}
                setSelectedSystem={setSelectedProcessSystem}
                showSystemModal={showSystemModal}
                setShowSystemModal={setShowSystemModal}
                showGuideModal={showGuideModal}
                setShowGuideModal={setShowGuideModal}
                showFaqModal={showFaqModal}
                setShowFaqModal={setShowFaqModal}
                showDocumentModal={showDocumentModal}
                setShowDocumentModal={setShowDocumentModal}
                systemForm={systemForm}
                setSystemForm={setSystemForm}
                guideForm={guideForm}
                setGuideForm={setGuideForm}
                faqForm={faqForm}
                setFaqForm={setFaqForm}
                editingSystem={editingSystem}
                setEditingSystem={setEditingSystem}
                editingGuide={editingGuide}
                setEditingGuide={setEditingGuide}
                editingFaq={editingFaq}
                setEditingFaq={setEditingFaq}
                uploadFile={uploadFile}
                setUploadFile={setUploadFile}
                uploadDescription={uploadDescription}
                setUploadDescription={setUploadDescription}
                uploading={uploading}
                setUploading={setUploading}
                handleCreateSystem={psHandleCreate}
                handleUpdateSystem={psHandleUpdate}
                handleCreateGuide={psHandleCreateGuide}
                handleUpdateGuide={psHandleUpdateGuide}
                handleCreateFaq={psHandleCreateFaq}
                handleUpdateFaq={psHandleUpdateFaq}
                handleUploadDocument={psHandleUploadDoc}
                handleDeleteDocument={psHandleDeleteDoc}
                handleDeleteSystem={psHandleDeleteSystem}
                refreshSystemDetails={(fn) => { psRefreshFn.current = fn; }}
              />
            )}
            {tab === 'erfolge' && (
              <ErfolgeTab role="backoffice" />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BackofficeDashboard;
